/**
 * Input in the portal, counted across every open tab (TASK-649).
 *
 * The time of the last input lives in localStorage, so input in one tab keeps
 * every tab signed in. It is a time, not a secret.
 *
 * Only a person's input counts. Background polling, auto-scroll, focus and
 * timers never call `recordInput`, so they never keep a session alive.
 */

export const LAST_INPUT_KEY = 'portal_last_input_at';

/** Write at most this often. The first input after a quiet spell is written at once. */
const WRITE_EVERY_MS = 5000;

/**
 * Counted: keys, clicks and taps, the wheel, text input with no key press
 * (dictation, paste from a menu, an input method), and a dropped file.
 * Not counted: pointer moves. On macOS a pointer passing over an unfocused
 * window sends them, and content moving under a still pointer can too.
 */
export const COUNTED_EVENTS = ['keydown', 'pointerdown', 'wheel', 'touchstart', 'beforeinput', 'drop'] as const;

/**
 * The class on the idle warning. Input inside it is not counted: a key or a
 * click there must reach the warning's buttons, not close the warning first.
 * "Stay signed in" records input itself.
 */
export const WARNING_CLASS = 'session-warning';

let lastWritten = 0;
/** This tab's own last input, for a browser that refuses localStorage. */
let lastInMemory: number | null = null;
const listeners = new Set<(at: number) => void>();

function readStored(): number | null {
  try {
    const raw = localStorage.getItem(LAST_INPUT_KEY);
    if (raw === null) return null;
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
  } catch {
    return null;
  }
}

/** The latest input in any tab, or in this tab when storage is refused. */
export function readLastInput(): number | null {
  const stored = readStored();
  if (stored === null) return lastInMemory;
  if (lastInMemory === null) return stored;
  return Math.max(stored, lastInMemory);
}

/** Record input now. `force` writes even inside the 5-second window (sign-in, "Stay signed in"). */
export function recordInput(now = Date.now(), force = false): void {
  if (!force && now - lastWritten < WRITE_EVERY_MS) return;
  lastWritten = now;
  lastInMemory = Math.max(lastInMemory ?? now, now);
  try {
    localStorage.setItem(LAST_INPUT_KEY, String(now));
  } catch {
    // Storage refused: this tab keeps the time in memory, other tabs do not see it.
  }
  for (const listener of listeners) listener(now);
}

/** Hear about input in this tab and in other tabs. Returns the unsubscribe. */
export function onInput(listener: (at: number) => void): () => void {
  listeners.add(listener);
  const fromOtherTab = (event: StorageEvent) => {
    if (event.key !== LAST_INPUT_KEY || event.newValue === null) return;
    const at = Number(event.newValue);
    if (Number.isFinite(at)) listener(at);
  };
  window.addEventListener('storage', fromOtherTab);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', fromOtherTab);
  };
}

/**
 * Count a person's input on this page. Capture phase on window, so a handler
 * that stops propagation does not hide input. `isCounted` defaults to the
 * browser's own mark that a person, not script, made the event.
 */
export function watchInput(isCounted: (event: Event) => boolean = (event) => event.isTrusted): () => void {
  const handler = (event: Event) => {
    const target = event.target;
    if (target instanceof Element && target.closest(`.${WARNING_CLASS}`)) return;
    if (isCounted(event)) recordInput();
  };
  for (const name of COUNTED_EVENTS) {
    window.addEventListener(name, handler, { capture: true, passive: true });
  }
  return () => {
    for (const name of COUNTED_EVENTS) {
      window.removeEventListener(name, handler, { capture: true });
    }
  };
}

/** Tests only: forget the write throttle. */
export function resetActivityForTests(): void {
  lastWritten = 0;
  lastInMemory = null;
  listeners.clear();
}
